/**
 * What: Audience Set profile-overlay schema shared by Audience Set compilation and analytics promotion.
 * Transport / data-flow: Pure Zod validation for run-scoped overlay fields before StudyRun planning.
 *
 */
import * as z from 'zod';
import { fieldGroundingStatusSchema, groundingSourceTypeSchema, profileOverlayRoleSchema, } from '@testfeed/core/contracts';
import { integrationAggregateProviderSchema, integrationAggregateSourceSurfaceSchema, } from '@testfeed/integration-aggregates/vocabulary';
import { SHOPIFY_RFM_GROUP_FRAME_FIELD } from './shopper-background-values.js';
import { validateShopifyRfmGroupFrameFingerprint, validateShopifyRfmGroupFrameProvenance, } from './shopify-rfm-frame-provenance.js';
export const audienceSetSignalKindValues = [
    'purchase_history',
    'spend_history',
    'product_purchase_interest',
    'product_browsing_interest',
    'collection_browsing_interest',
    'high_intent_path',
    'device_context',
    'geo_context',
    'source_channel_context',
    'product_interest',
    'intent_level',
    'audience_shape',
    'cohort_context',
    'discovery_intent',
];
export const audienceSetSignalKindSchema = z.enum(audienceSetSignalKindValues);
const profileOverlayStringMapSchema = z
    .object({})
    .catchall(z.string().trim().min(1))
    .refine((value) => Object.keys(value).every((key) => key.trim().length > 0), {
    message: 'profile overlay string map keys must be non-empty',
});
/** Provenance is part of both the wire and stored StudySpec; neither may strip it. */
export const audienceSetProfileOverlayProvenanceShape = {
    analytics_source: integrationAggregateProviderSchema.optional(),
    signal_kind: audienceSetSignalKindSchema.optional(),
    source_scope_type: integrationAggregateProviderSchema.optional(),
    formula_name: z.string().trim().min(1).optional(),
    formula_version: z.string().trim().min(1).optional(),
    source_buckets: z
        .array(z
        .object({
        provider: integrationAggregateProviderSchema,
        source_surface: integrationAggregateSourceSurfaceSchema,
        bucket_start: z.string().datetime({ offset: true }),
        bucket_end: z.string().datetime({ offset: true }),
        record_ids: z.array(z.string().trim().min(1)).min(1),
    })
        .strict())
        .optional(),
    caveats: z.array(z.string().trim().min(1)).optional(),
    frame_fingerprint: z.string().trim().min(1).optional(),
    shopify_shop_domain: z.string().trim().min(1).optional(),
};
/** Shopify aggregate frames are not formula-derived analytics. Never invent bucket provenance for them. */
export function classifyProfileOverlayProvenance(field) {
    const hasFormulaProvenance = Boolean(field.formula_name || field.formula_version || field.source_buckets);
    if (field.field === SHOPIFY_RFM_GROUP_FRAME_FIELD) {
        const errors = validateShopifyRfmGroupFrameProvenance({
            provider: field.analytics_source,
            signalKind: field.signal_kind,
            sourceScopeType: field.source_scope_type,
            frameScope: field.frame_scope,
            derivationMethod: field.derivation_method,
        });
        return !hasFormulaProvenance &&
            field.derivation_method === 'framed_to_shopify_customer_group' &&
            errors.length === 0 &&
            validateShopifyRfmGroupFrameFingerprint(field.frame_fingerprint).length === 0
            ? 'frame_only'
            : 'incomplete';
    }
    if (!field.analytics_source && !field.signal_kind && !hasFormulaProvenance)
        return 'aggregate';
    if (field.analytics_source === 'shopify' && field.signal_kind && !hasFormulaProvenance)
        return 'frame_only';
    if (field.analytics_source &&
        field.signal_kind &&
        field.formula_name &&
        field.formula_version &&
        field.source_buckets?.length)
        return 'formula';
    return 'incomplete';
}
export const audienceSetProfileOverlayFieldSchema = z
    .object({
    field: z
        .string()
        .trim()
        .min(1)
        .regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().trim().min(1),
    source_type: groundingSourceTypeSchema,
    ...audienceSetProfileOverlayProvenanceShape,
    grounding_status: fieldGroundingStatusSchema,
    frame_scope: z.string().trim().min(1),
    derivation_method: z.string().trim().min(1).optional(),
    allocation: z.object({
        method: z.literal('seeded_marginal'),
        target_distribution: z.record(z.string(), z.number().min(0)),
    }),
    value_labels: profileOverlayStringMapSchema.optional(),
    prompt_instructions: profileOverlayStringMapSchema.optional(),
    allowed_roles: z.array(profileOverlayRoleSchema).min(1),
    blocked_roles: z.array(z.string().trim().min(1)).optional(),
    claim_boundary: z.string().trim().min(1),
})
    .strict()
    .superRefine((field, context) => {
    if (field.field === SHOPIFY_RFM_GROUP_FRAME_FIELD) {
        const distributionEntries = Object.entries(field.allocation.target_distribution);
        const total = distributionEntries.reduce((sum, [, value]) => sum + value, 0);
        if (distributionEntries.length === 0 || Math.abs(total - 100) > 0.01) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Shopify customer-group frame allocation must contain a complete 100% target distribution',
                path: ['allocation', 'target_distribution'],
            });
        }
    }
    if (classifyProfileOverlayProvenance(field) === 'incomplete') {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Profile overlay requires complete customer-group frame provenance or formula_name, formula_version, and source_buckets',
            path: ['source_buckets'],
        });
    }
});
export const audienceSetProfileOverlaySchema = z
    .object({
    fields: z.array(audienceSetProfileOverlayFieldSchema).min(1),
})
    .refine((value) => new Set(value.fields.map((field) => field.field)).size === value.fields.length, {
    message: 'profile overlay field names must be unique',
    path: ['fields'],
});
//# sourceMappingURL=audience-set-profile-overlay.js.map